import {
  Component,
  ViewEncapsulation,
  OnInit,
  OnDestroy,
  Injector,
  ChangeDetectorRef,
} from '@angular/core';
import {
  Subject,
  takeUntil,
  debounceTime,
  distinctUntilChanged,
  skip,
  BehaviorSubject,
} from 'rxjs';
import { FormBuilderService } from '../../../../core/services/form-builder.service';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { FieldConfigTypeAsStringPipe } from '../../../../core/pipes/field-config-type-as-string.pipe';
import {
  EIremboFormlyFieldSubTypes,
  EIremboFormlyFieldTypes,
  IIrembogovBasicLabelKeyPair,
  TIdType,
} from '@irembo-andela/irembogov3-common';
import {
  IFormbuilderFieldTypeConfiguration,
  TConfigurationFormModelTypes,
} from '../../../../core/models/form-builder-types-configurations.model';
import { FormGroup, FormArray, AbstractControl } from '@angular/forms';
import { FormBuilderManipulatorClass } from '../../../../core/utils/form-builder-manipulator-class.utils';
import { IFormFieldsValidationModel } from '../../../../core/models/form-fields-formly-validation.model';
import { FormBuilderFieldConfigurationParserAndSetterClass } from '../../../../core/utils/form-fields-formly-configurations/form-builder-configuration-parser-and-setter.class';
import { IFormFieldsDisplayModel } from '../../../../core/models/form-fields-formly-display.model';
import { ViewportScroller } from '@angular/common';
import { BaseFormlyFieldConfiguration } from '../../../../core/utils/form-fields-formly-configurations/form-field-formly-types-main-configurations/form-field-formly-configuration-classes/base-formly-field-configuration';
import { getFormFieldConfigurationClass } from '../../../../core/utils/form-fields-formly-configurations/form-field-formly-types-main-configurations/form-field-formly-configuration-classes/utils/get-form-field-configuration-class';

enum EConfigurationTabTemplateKeys {
  Settings = 'Settings',
  Validation = 'Validation',
  Display = 'Display',
}

@Component({
  selector: 'irembogov-form-field-configurator-settings',
  templateUrl: './form-field-configurator-settings.component.html',
  styleUrls: ['./form-field-configurator-settings.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class FormFieldConfiguratorSettingsComponent
  implements OnInit, OnDestroy
{
  configurationTabs: IIrembogovBasicLabelKeyPair<EConfigurationTabTemplateKeys>[] =
    [];

  selectedConfigurationTab: EConfigurationTabTemplateKeys =
    EConfigurationTabTemplateKeys.Settings;

  EConfigurationTabTemplateKeys = EConfigurationTabTemplateKeys;

  EIremboFormlyFieldTypes = EIremboFormlyFieldTypes;

  configuredField!: FormlyFieldConfig | null;

  fieldConfigTypeAsStringPipe: FieldConfigTypeAsStringPipe =
    new FieldConfigTypeAsStringPipe();

  fieldType?: string;

  configureFieldActionSubControl$: Subject<void> = new Subject<void>();

  fieldConfigurationConfig!: IFormbuilderFieldTypeConfiguration;

  settingsForm: FormGroup = new FormGroup({});
  settingsFormErrorFields: string[] = [];
  settingsModel: TConfigurationFormModelTypes = {};

  validationForm: FormGroup = new FormGroup({});
  validationModel: IFormFieldsValidationModel = {};

  formlyFormOptions: Record<string, unknown> = {
    formState: { hideBuilderWrapper: true },
  };

  displayForm: FormGroup = new FormGroup({});
  displayModel: IFormFieldsDisplayModel = {};
  formBuilderManipulator!: FormBuilderManipulatorClass;
  configurationParserAndSetterClass!: FormBuilderFieldConfigurationParserAndSetterClass;
  fieldsWithNewConfigurations: EIremboFormlyFieldTypes[] = [
    EIremboFormlyFieldTypes.checkbox,
    EIremboFormlyFieldTypes.input,
    EIremboFormlyFieldTypes.textarea,
    EIremboFormlyFieldTypes.customdropdown,
    EIremboFormlyFieldTypes.customdropdownpaginated,
    EIremboFormlyFieldTypes.multicheckbox,
    EIremboFormlyFieldTypes.radio,
    EIremboFormlyFieldTypes.customidinput,
    EIremboFormlyFieldTypes.customphonenumber,
    EIremboFormlyFieldTypes.customdatepicker,
    EIremboFormlyFieldTypes.customfileupload,
    EIremboFormlyFieldTypes.customcascadingdropdowns,
    EIremboFormlyFieldTypes.customslotbooking,
    EIremboFormlyFieldTypes.customtininput,
    EIremboFormlyFieldTypes.customcrvsidinput,
    EIremboFormlyFieldTypes.customgenericdatafetch,
    EIremboFormlyFieldTypes.customapplicationnumberinput,
    EIremboFormlyFieldTypes.customsectionmessagewithlink,
    EIremboFormlyFieldTypes.customdoubleidinput,
    EIremboFormlyFieldTypes.customdatasettable,
    EIremboFormlyFieldTypes.customdatatable,
    EIremboFormlyFieldTypes.customdynamicrepeater,
    EIremboFormlyFieldTypes.customdatetimepicker,
    EIremboFormlyFieldTypes.customerrorcomponent,
    EIremboFormlyFieldTypes.customtimepicker,
    EIremboFormlyFieldTypes.customcurrencyformatinput,
    EIremboFormlyFieldTypes.customotpdatafetch,
    EIremboFormlyFieldTypes.customrepeater,
    EIremboFormlyFieldTypes.customtablewithinputform,
  ];
  newFormlyFieldConfiguration!: BaseFormlyFieldConfiguration;
  isConfiguredFieldHasNewConfigurationClass = false;
  private fieldConfigChange$ = new Subject<void>();
  autoSaveInProgress = false;
  lastAutoSaved: Date | null = null;
  autoSaveTimeout: any = null;
  formChanges$ = new BehaviorSubject<boolean>(false);

  constructor(
    private formBuilderService: FormBuilderService,
    private injector: Injector,
    private cdRef: ChangeDetectorRef,
    private readonly viewPortScroller: ViewportScroller
  ) {
    this.formBuilderManipulator = new FormBuilderManipulatorClass(
      this.injector
    );
    this.configurationParserAndSetterClass =
      new FormBuilderFieldConfigurationParserAndSetterClass(this.injector);
  }

  ngOnInit(): void {
    this.formBuilderService.configureFieldAction$
      .pipe(takeUntil(this.configureFieldActionSubControl$))
      .subscribe((configuredField: FormlyFieldConfig | null) => {
        this.configuredField = configuredField;
        this.setConfigTabsForType();
        this.setupAutoSave();
      });

    this.configuredField =
      this.formBuilderService.configureFieldAction$.getValue();
    this.setConfigTabsForType();
    this.setupAutoSave();
  }

  setConfigTabsForType(): void {
    if (!this.configuredField) return;
    this.configurationTabs = [];
    this.displayModel = {};
    this.validationModel = {};
    this.displayModel = {};
    this.fieldType = this.fieldConfigTypeAsStringPipe.transform(
      this.configuredField
    );

    let subtype: EIremboFormlyFieldSubTypes | undefined;

    if (this.fieldType === EIremboFormlyFieldTypes.customidinput) {
      const url: string | undefined = this.configuredField?.props?.['url'];
      const idType: TIdType | undefined =
        this.configuredField.props?.['idType'];

      if (idType) {
        switch (idType) {
          case 'NIN':
            subtype = EIremboFormlyFieldSubTypes.CUSTOM_ID_INPUT_NIN;
            break;
          case 'NID':
            subtype = EIremboFormlyFieldSubTypes.CUSTOM_ID_INPUT_NID;
            break;
          case 'CHILD_ID':
            subtype = EIremboFormlyFieldSubTypes.CUSTOM_ID_INPUT_CHILD_ID;
            break;
          default:
            break;
        }
      } else {
        switch (url) {
          case url && url.toLowerCase().indexOf('identity/v1/nin-info') > -1:
            subtype = EIremboFormlyFieldSubTypes.CUSTOM_ID_INPUT_NIN;
            break;
          case url && url.toLowerCase().indexOf('identity/v1/nid-info') > -1:
            subtype = EIremboFormlyFieldSubTypes.CUSTOM_ID_INPUT_NID;
            break;
          case url &&
            url.toLowerCase().indexOf('identity/v1/child-id-info') > -1:
            subtype = EIremboFormlyFieldSubTypes.CUSTOM_ID_INPUT_CHILD_ID;
            break;
          default:
            break;
        }
      }
    }

    if (
      this.fieldType === EIremboFormlyFieldTypes.customdropdownpaginated &&
      this.configuredField.props?.['subType'] ===
        EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATAFETCH_PAGINATED
    ) {
      subtype = EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATAFETCH_PAGINATED;
    }

    if (
      this.fieldType === EIremboFormlyFieldTypes.customdropdown &&
      this.configuredField.props?.['subType'] ===
        EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATAFETCH
    ) {
      subtype = EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATAFETCH;
    }

    if (
      this.fieldType === EIremboFormlyFieldTypes.customdropdownpaginated &&
      this.configuredField.props?.['subType'] ===
        EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATASET_PAGINATED
    ) {
      subtype = EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATASET_PAGINATED;
    }

    if (
      this.fieldType === EIremboFormlyFieldTypes.customdropdown &&
      this.configuredField.props?.['subType'] ===
        EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATASET
    ) {
      subtype = EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATASET;
    }

    // TODO: Remove these checks after configuring all components to use the new configuration classes
    this.isConfiguredFieldHasNewConfigurationClass =
      this.fieldsWithNewConfigurations.includes(
        this.configuredField.type as EIremboFormlyFieldTypes
      );
    this.newFormlyFieldConfiguration = getFormFieldConfigurationClass(
      this.injector,
      this.configuredField
    );
    this.fieldConfigurationConfig = this
      .isConfiguredFieldHasNewConfigurationClass
      ? this.newFormlyFieldConfiguration.getFormlyFieldConfigurationConfig()
      : this.configurationParserAndSetterClass.getFormlyFieldConfigurationConfigByType(
          this.fieldType,
          subtype
        );

    if (this.fieldConfigurationConfig.settings) {
      this.configurationTabs.push({
        label: 'Settings',
        key: EConfigurationTabTemplateKeys.Settings,
        icon: 'fa-solid fa-gear',
      });
      this.settingsModel = this.isConfiguredFieldHasNewConfigurationClass
        ? this.newFormlyFieldConfiguration.parseFieldConfigIntoSettingsFormModel()
        : this.configurationParserAndSetterClass.parseFieldIntoSettingsFormModelByType(
            this.configuredField,
            this.fieldType
          );
    }

    if (this.fieldConfigurationConfig.validations) {
      this.fieldConfigurationConfig.validations.formlyForm.push(
        ...this.generateConfiguredFieldModelAndRefPathsFormlyFields()
      );
      this.configurationTabs.push({
        label: 'Validation',
        key: EConfigurationTabTemplateKeys.Validation,
        icon: 'fa-regular fa-circle-check',
      });
      this.validationModel = this.isConfiguredFieldHasNewConfigurationClass
        ? this.newFormlyFieldConfiguration.parseFieldConfigIntoValidationsFormModel()
        : this.configurationParserAndSetterClass.parseFieldIntoValidationFormModelByType(
            this.configuredField,
            this.fieldType
          );
    }

    if (this.fieldConfigurationConfig.display) {
      this.configurationTabs.push({
        label: 'Display',
        key: EConfigurationTabTemplateKeys.Display,
        icon: 'fa-solid fa-palette',
      });
      this.displayModel = this.isConfiguredFieldHasNewConfigurationClass
        ? this.newFormlyFieldConfiguration.parseFieldConfigIntoDisplayFormModel()
        : this.configurationParserAndSetterClass.parseFieldIntoDisplayFormModel(
            this.configuredField,
            this.fieldType
          );
    }

    this.cdRef.detectChanges();
  }

  private generateConfiguredFieldModelAndRefPathsFormlyFields(): FormlyFieldConfig[] {
    const configuredFieldLabelAndDetails =
      this.formBuilderService.getCurrentConfiguredFieldLabelAndDetails();
    return [
      {
        key: 'configuredFieldRootModelPath',
        type: EIremboFormlyFieldTypes.input,
        defaultValue: configuredFieldLabelAndDetails?.rootModelPath,
        props: {
          readonly: true,
          hideField: true,
        },
      },
      {
        key: 'configuredFieldRefPath',
        type: EIremboFormlyFieldTypes.input,
        defaultValue: configuredFieldLabelAndDetails?.fieldRefPath,
        props: {
          readonly: true,
          hideField: true,
        },
      },
    ];
  }

  private getFormlyFieldConfigurationsByType(): void {
    if (!this.configuredField) return;
    let subtype: EIremboFormlyFieldSubTypes | undefined;

    if (this.fieldType === EIremboFormlyFieldTypes.customidinput) {
      const url: string | undefined = this.configuredField?.props?.['url'];
      switch (url) {
        case url && url.toLowerCase().indexOf('identity/v1/nin-info') > -1:
          subtype = EIremboFormlyFieldSubTypes.CUSTOM_ID_INPUT_NIN;
          break;
        case url && url.toLowerCase().indexOf('identity/v1/nid-info') > -1:
          subtype = EIremboFormlyFieldSubTypes.CUSTOM_ID_INPUT_NID;
          break;
        case url && url.toLowerCase().indexOf('identity/v1/child-id-info') > -1:
          subtype = EIremboFormlyFieldSubTypes.CUSTOM_ID_INPUT_CHILD_ID;
      }
    }
    if (
      this.fieldType === EIremboFormlyFieldTypes.customdropdown &&
      this.configuredField.props?.['subType'] ===
        EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATAFETCH
    ) {
      subtype = EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATAFETCH;
    }

    if (
      this.fieldType === EIremboFormlyFieldTypes.customdropdown &&
      this.configuredField.props?.['subType'] ===
        EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATASET
    ) {
      subtype = EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATASET;
    }

    this.fieldConfigurationConfig =
      this.configurationParserAndSetterClass.getFormlyFieldConfigurationConfigByType(
        this.fieldType,
        subtype
      );
  }

  private getNamesOfFormControlsWithErrors(
    formControl: AbstractControl | null,
    formControlName: string
  ): string[] {
    const errorNames: string[] = [];
    if (!formControl) return errorNames;
    if (
      formControl.invalid &&
      !(formControl instanceof FormGroup || formControl instanceof FormArray)
    ) {
      errorNames.push(formControlName);
    }

    const parentingName =
      formControlName === '_' ? '' : formControlName + ' -> ';

    if (formControl instanceof FormGroup) {
      formControl.markAllAsTouched();
      Object.keys(formControl.controls).forEach(key => {
        if (formControl.get(key)?.invalid) {
          formControl.get(key)?.markAllAsTouched();
          const formGroupErrorNames: string[] =
            this.getNamesOfFormControlsWithErrors(
              formControl.get(key),
              `${parentingName}${key}`
            );
          errorNames.push(...formGroupErrorNames);
        }
      });
    } else if (formControl instanceof FormArray) {
      formControl.controls.forEach((ctrl: AbstractControl, index: number) => {
        ctrl.markAllAsTouched();
        const formArrayErrorNames: string[] =
          this.getNamesOfFormControlsWithErrors(
            ctrl,
            `${parentingName}[${index}]`
          );
        errorNames.push(...formArrayErrorNames);
      });
    }

    return errorNames;
  }

  generateErrorMessages(form: FormGroup): string[] {
    const errorMessages: string[] = [];
    if (!form) return errorMessages;

    const errorNames: string[] = this.getNamesOfFormControlsWithErrors(
      form,
      '_'
    );
    errorNames.forEach(name => {
      const formattedName = name.replace(/([A-Z])/g, ' $1'); // convert from camel case to normal text
      const capitalizedName =
        formattedName.charAt(0).toUpperCase() + formattedName.slice(1);
      // Add the formatted name to the error messages
      errorMessages.push(capitalizedName);
    });

    return errorMessages;
  }

  saveFieldConfigurationSettings(isAutoSave = false) {
    this.settingsFormErrorFields = [];
    if (!(this.configuredField?.props && this.settingsForm.valid)) {
      this.settingsFormErrorFields = this.generateErrorMessages(
        this.settingsForm
      );
      this.viewPortScroller.scrollToAnchor('serviceConfigurationErrorMessage');
      return;
    }

    if (
      this.configuredField.type ===
      EIremboFormlyFieldTypes.customcascadingdropdowns
    ) {
      const updatedFields: FormlyFieldConfig[] =
        this.configurationParserAndSetterClass.generateWidgetFieldsFromSettingsFormModel(
          this.settingsModel,
          this.configuredField,
          this.fieldType
        );
      this.formBuilderManipulator.replaceConfiguredFieldItemWithFields(
        updatedFields
      );
      this.formBuilderService.configureFieldAction$.next(null);
      return;
    }

    this.configuredField =
      this.configurationParserAndSetterClass.updateFieldFromSettingsFormModel(
        this.settingsModel,
        this.configuredField,
        this.fieldType
      );
    this.formBuilderManipulator.updateConfiguredFieldItem(
      this.configuredField,
      isAutoSave
    );
    if (!isAutoSave) {
      this.formBuilderService.configureFieldAction$.next(null);
    }
  }

  saveFieldConfigurationValidation(isAutoSave = false) {
    if (!(this.configuredField?.props && this.validationForm.valid)) return;

    this.configuredField = this.isConfiguredFieldHasNewConfigurationClass
      ? this.newFormlyFieldConfiguration.updateFieldConfigFromValidationsFormModel(
          this.validationModel
        )
      : this.configurationParserAndSetterClass.updateFieldFromValidationsFormModel(
          this.validationModel,
          this.configuredField,
          this.fieldType
        );

    this.formBuilderManipulator.updateConfiguredFieldItem(
      this.configuredField,
      isAutoSave
    );
    if (!isAutoSave) {
      this.formBuilderService.configureFieldAction$.next(null);
    }
  }

  saveFieldConfigurationDisplay(isAutoSave = false) {
    if (!(this.configuredField?.props && this.displayForm.valid)) return;

    this.configuredField =
      this.configurationParserAndSetterClass.updateFieldFromDisplayFormModel(
        this.displayModel,
        this.configuredField,
        this.fieldType
      );

    this.formBuilderManipulator.updateConfiguredFieldItem(
      this.configuredField,
      isAutoSave
    );
    if (!isAutoSave) {
      this.formBuilderService.configureFieldAction$.next(null);
    }
  }

  private setupAutoSave(): void {
    const deepCompare = (prev: any, curr: any) =>
      JSON.stringify(prev) === JSON.stringify(curr);

    const commonPipeOps = [
      debounceTime(5000),
      distinctUntilChanged(deepCompare),
      skip(1),
      takeUntil(this.fieldConfigChange$),
    ] as const;

    // Field Settings auto-save
    if (this.settingsForm) {
      this.settingsForm.valueChanges.pipe(...commonPipeOps).subscribe(() => {
        if (this.settingsForm.valid) {
          this.simulateAutoSave(() =>
            this.saveFieldConfigurationSettings(true)
          );
        }
      });
    }

    // Validation rules auto-save
    if (this.validationForm) {
      this.validationForm.valueChanges.pipe(...commonPipeOps).subscribe(() => {
        if (this.validationForm.valid) {
          this.simulateAutoSave(() =>
            this.saveFieldConfigurationValidation(true)
          );
        }
      });
    }

    // Display rules auto-save
    if (this.displayForm) {
      this.displayForm.valueChanges.pipe(...commonPipeOps).subscribe(() => {
        if (this.displayForm.valid) {
          this.simulateAutoSave(() => this.saveFieldConfigurationDisplay(true));
        }
      });
    }
  }

  private simulateAutoSave(saveAction: () => void): void {
    if (this.autoSaveInProgress) return;

    this.autoSaveInProgress = true;
    const saveDelay = 1000;
    setTimeout(() => {
      try {
        saveAction();
        this.lastAutoSaved = new Date();
        this.formChanges$.next(false);
      } catch (error) {
        console.error('Auto-save failed:', error);
        if (this.autoSaveTimeout) {
          clearTimeout(this.autoSaveTimeout);
        }
        this.autoSaveTimeout = setTimeout(() => {
          this.formChanges$.next(true);
        }, 10000);
      } finally {
        this.autoSaveInProgress = false;
        this.cdRef.markForCheck();
      }
    }, saveDelay);
  }

  ngOnDestroy(): void {
    this.fieldConfigChange$.next();
    this.fieldConfigChange$.complete();
    this.configureFieldActionSubControl$.next();
    this.configureFieldActionSubControl$.complete();
  }
}
